import { useState, useEffect, useCallback, useRef } from 'react';
import { api as apiClient } from '../core/api-client';
import type { AxiosRequestConfig } from 'axios';

// =============================================================================
// Types
// =============================================================================

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';

export interface UseApiOptions<TBody = unknown> {
  /** HTTP method for the request */
  method?: HttpMethod;
  /** Request body for POST/PUT/PATCH requests */
  body?: TBody;
  /** URL query parameters */
  params?: Record<string, string | number | boolean | undefined>;
  /** Additional request headers */
  headers?: Record<string, string>;
  /** Auto-fetch on mount (default true for GET, false for mutations) */
  immediate?: boolean;
  /** Callback on successful response */
  onSuccess?: (data: unknown) => void;
  /** Callback on error */
  onError?: (error: Error) => void;
  /** Reset data/error state before each execute */
  resetOnExecute?: boolean;
}

export interface ExecuteOptions<TBody = unknown> {
  /** Override request body */
  body?: TBody;
  /** Override URL query parameters */
  params?: Record<string, string | number | boolean | undefined>;
  /** Override request headers */
  headers?: Record<string, string>;
  /** Override the URL for this request */
  url?: string;
}

export interface UseApiReturn<TData, TBody = unknown> {
  /** Response data (null initially) */
  data: TData | null;
  /** Whether a request is in flight */
  loading: boolean;
  /** Error object if request failed */
  error: Error | null;
  /** Re-run the original request */
  refetch: () => Promise<TData | null>;
  /** Manually trigger request */
  execute: (options?: ExecuteOptions<TBody>) => Promise<TData | null>;
  /** Cancel in-flight request */
  abort: () => void;
  /** Whether the last request was aborted */
  isAborted: boolean;
}

// =============================================================================
// Main Hook
// =============================================================================

/**
 * Custom hook for making API calls with automatic state management,
 * request cancellation, and cleanup on unmount.
 *
 * Features:
 * - Manages loading, error, and data states
 * - AbortController for request cancellation
 * - Automatic cleanup on component unmount
 * - Cancels previous request when new one is made
 * - Supports GET, POST, PUT, DELETE, PATCH methods
 * - Automatic fetch on mount for GET requests
 * - Manual execute function for mutations
 * - Refetch capability
 * - Success and error callbacks
 *
 * Authentication is handled automatically by the shared Axios instance
 * (core/api-client.ts) which injects the Bearer token and handles 401
 * refresh. No auth logic needed in this hook.
 *
 * @example
 * // GET request (auto-fetches on mount)
 * const { data, loading, error, refetch } = useApi<User[]>('/users');
 *
 * @example
 * // POST request (manual trigger)
 * const { execute, loading, error } = useApi<User, CreateUserDto>('/users', {
 *   method: 'POST',
 *   immediate: false,
 *   onSuccess: (data) => console.log('Created:', data)
 * });
 * await execute({ body: { name: 'New User' } });
 *
 * @example
 * // Search with abort (cancel previous request)
 * const { execute, abort } = useApi<SearchResult[]>('/search', { immediate: false });
 * const handleSearch = (query: string) => {
 *   abort();
 *   execute({ params: { q: query } });
 * };
 */
export function useApi<TData = unknown, TBody = unknown>(
  url: string,
  options: UseApiOptions<TBody> = {}
): UseApiReturn<TData, TBody> {
  const {
    method = 'GET',
    body: initialBody,
    params: initialParams,
    headers: initialHeaders,
    immediate,
    onSuccess,
    onError,
    resetOnExecute = false,
  } = options;

  // Determine if we should auto-fetch
  // Default: true for GET, false for mutations
  const shouldAutoFetch = immediate ?? method === 'GET';

  // State
  const [data, setData] = useState<TData | null>(null);
  const [loading, setLoading] = useState<boolean>(shouldAutoFetch);
  const [error, setError] = useState<Error | null>(null);
  const [isAborted, setIsAborted] = useState<boolean>(false);

  // Refs for cleanup and tracking
  const abortControllerRef = useRef<AbortController | null>(null);
  const mountedRef = useRef<boolean>(true);
  const requestIdRef = useRef<number>(0);

  // Store latest callbacks in refs to avoid stale closures
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);

  // Keep callback refs updated
  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
  }, [onSuccess, onError]);

  /**
   * Aborts any in-flight request
   */
  const abort = useCallback((): void => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  /**
   * Executes the API request
   */
  const execute = useCallback(
    async (executeOptions: ExecuteOptions<TBody> = {}): Promise<TData | null> => {
      const {
        body: executeBody,
        params: executeParams,
        headers: executeHeaders,
        url: executeUrl,
      } = executeOptions;

      // Cancel any in-flight request
      abort();

      // Create new AbortController for this request
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      // Increment request ID to track stale responses
      const currentRequestId = ++requestIdRef.current;

      // Reset state if configured
      if (resetOnExecute) {
        setData(null);
        setError(null);
      }

      setLoading(true);
      setIsAborted(false);
      setError(null);

      try {
        // Build request config
        const requestConfig: AxiosRequestConfig = {
          method,
          url: executeUrl ?? url,
          signal: abortController.signal,
          headers: {
            ...initialHeaders,
            ...executeHeaders,
          },
        };

        // Add body for non-GET requests
        const bodyData = executeBody ?? initialBody;
        if (bodyData !== undefined && method !== 'GET') {
          requestConfig.data = bodyData;
        }

        // Add params
        const paramsData = executeParams ?? initialParams;
        if (paramsData !== undefined) {
          requestConfig.params = paramsData;
        }

        const response = await apiClient.request<TData>(requestConfig);

        // Check if this is still the current request and component is mounted
        if (currentRequestId !== requestIdRef.current) {
          return null; // Stale response, ignore
        }

        if (!mountedRef.current) {
          return null; // Component unmounted, ignore
        }

        // apiClient returns response.data directly via interceptor
        const responseData = response as unknown as TData;
        setData(responseData);
        setLoading(false);

        // Call success callback
        if (onSuccessRef.current) {
          onSuccessRef.current(responseData);
        }

        return responseData;
      } catch (err) {
        // Check if this is still the current request and component is mounted
        if (currentRequestId !== requestIdRef.current) {
          return null; // Stale response, ignore
        }

        if (!mountedRef.current) {
          return null; // Component unmounted, ignore
        }

        // Handle abort specifically
        if (
          err instanceof Error &&
          (err.name === 'AbortError' || err.name === 'CanceledError' || abortController.signal.aborted)
        ) {
          setIsAborted(true);
          setLoading(false);
          return null;
        }

        // Normalize error
        const normalizedError =
          err instanceof Error
            ? err
            : new Error((err as { message?: string })?.message || 'Request failed');

        setError(normalizedError);
        setLoading(false);

        // Call error callback
        if (onErrorRef.current) {
          onErrorRef.current(normalizedError);
        }

        return null;
      }
    },
    [url, method, initialBody, initialParams, initialHeaders, resetOnExecute, abort]
  );

  /**
   * Re-runs the original request with initial options
   */
  const refetch = useCallback((): Promise<TData | null> => {
    return execute();
  }, [execute]);

  // Auto-fetch on mount if immediate is true
  useEffect(() => {
    if (shouldAutoFetch) {
      execute();
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
      // Abort any in-flight request on unmount
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    };
  }, []);

  return {
    data,
    loading,
    error,
    refetch,
    execute,
    abort,
    isAborted,
  };
}

// =============================================================================
// Convenience Hooks
// =============================================================================

/**
 * Convenience hook for GET requests
 */
export function useGet<TData = unknown>(
  url: string,
  options: Omit<UseApiOptions<never>, 'method' | 'body'> = {}
): UseApiReturn<TData, never> {
  return useApi<TData, never>(url, { ...options, method: 'GET' });
}

/**
 * Convenience hook for POST requests (immediate: false by default)
 */
export function usePost<TData = unknown, TBody = unknown>(
  url: string,
  options: Omit<UseApiOptions<TBody>, 'method'> = {}
): UseApiReturn<TData, TBody> {
  return useApi<TData, TBody>(url, {
    ...options,
    method: 'POST',
    immediate: options.immediate ?? false,
  });
}

/**
 * Convenience hook for PUT requests (immediate: false by default)
 */
export function usePut<TData = unknown, TBody = unknown>(
  url: string,
  options: Omit<UseApiOptions<TBody>, 'method'> = {}
): UseApiReturn<TData, TBody> {
  return useApi<TData, TBody>(url, {
    ...options,
    method: 'PUT',
    immediate: options.immediate ?? false,
  });
}

/**
 * Convenience hook for DELETE requests (immediate: false by default)
 */
export function useDelete<TData = unknown>(
  url: string,
  options: Omit<UseApiOptions<never>, 'method' | 'body'> = {}
): UseApiReturn<TData, never> {
  return useApi<TData, never>(url, {
    ...options,
    method: 'DELETE',
    immediate: options.immediate ?? false,
  });
}

/**
 * Convenience hook for PATCH requests (immediate: false by default)
 */
export function usePatch<TData = unknown, TBody = unknown>(
  url: string,
  options: Omit<UseApiOptions<TBody>, 'method'> = {}
): UseApiReturn<TData, TBody> {
  return useApi<TData, TBody>(url, {
    ...options,
    method: 'PATCH',
    immediate: options.immediate ?? false,
  });
}

export default useApi;